import AlertBox from "@components/AlertBox/AlertBox"
import Header from "@components/Header/Header"
import JIDRContainerDisplay from "@components/JIDRConfirmationDisplay/JIDRContainerDisplay"
import PrimaryButton from "@components/PrimaryButton/PrimaryButton"
import AccountListItem from "@components/AccountListItem/AccountListItem"
import SectionLabel from "@components/SectionLabel/SectionLabel"
import { useNavigate, useLocation } from "react-router"
import { CRIMSON_500 } from "@styles/colors"
import { PageContainer, ContentContainer, DetailsContainer, SelectedAccountContainer, AlertContainer, FixedButtonContainer, ProviderInformationBoxText, DetailsBox } from "./RedeemConfirmation.styles"
import { RedemptionOption } from "../../types/redemption"
import ConfirmationInformationRow from "@components/ConfirmationInformationRow/ConfirmationInformationRow"
import { useTranslation } from "react-i18next"
import { ModalWithIcon } from "@components/ModalWithIcon/ModalWithIcon"
import { useState } from "react"
import LoadingModal from "@components/LoadingModal/LoadingModal"
import useOngoingTransaction from "@hooks/useOngoingTransaction"
import TemporaryTokenMigrationGuard from "@components/TemporaryTokenMigration/TemporaryTokenMigrationGuard/TemporaryTokenMigrationGuard"

const RedeemConfirmation = ({ type }: { type: 'bank' | 'e-wallet' }) => {
    const { data: ongoingTransactionInformation, isLoading } = useOngoingTransaction()
    const [showOnGoingTransactionModal, setShowOnGoingTransactionModal] = useState(false)

    const navigate = useNavigate()
    const location = useLocation()
    const { t } = useTranslation()

    const { redemptionTarget, amount, memo } = location.state && JSON.parse(location.state?.redeemInformation) as RedemptionOption

    const requestRedeem = () => {
        if (!ongoingTransactionInformation?.canCreateNewTransaction) {
            setShowOnGoingTransactionModal(true)
            return
        }
        navigate('/authentication', {
            state: {
                operationData: JSON.stringify({
                    type,
                    redemptionTarget,
                    amount,
                    memo
                }),
                operationType: 'redeem',
                headerTitle: t('redeem.confirmation.authentication.headerTitle'),
                description: t('redeem.confirmation.authentication.description'),
                useBiometrics: true
            }
        })
    }

    const handleBack = () => navigate(`/redeem?tab=${type}`, {
        state: type === 'bank' ?
            {
                bank_uuid: redemptionTarget.id,
                amount,
            }
            :
            {
                ewallet_uuid: redemptionTarget.id,
                amount,
            }
    })

    return (
        <PageContainer>
            <Header title={t('redeem.header')} showBackButton onBackClick={handleBack} />
            <LoadingModal visible={isLoading} />
            <TemporaryTokenMigrationGuard />
            <ContentContainer>
                {showOnGoingTransactionModal && <div style={{ zIndex: 2000 }}>
                    <ModalWithIcon
                        title={t('common.ongoingTransaction.title')}
                        description={t('common.ongoingTransaction.description')}
                        onPositive={handleBack}
                        positiveText={t('common.ongoingTransaction.positiveText')}
                        type="void"
                        visible={showOnGoingTransactionModal}
                        onModalOverlayClick={() => setShowOnGoingTransactionModal(false)}
                    />
                </div>}
                <JIDRContainerDisplay amount={amount} />
                <SelectedAccountContainer>
                    <ProviderInformationBoxText>
                        {type === 'bank' ? t('redeem.confirmation.selectedAccount.bank') : t('redeem.confirmation.selectedAccount.eWallet')}
                    </ProviderInformationBoxText>
                    <AccountListItem
                        oneLine={type === 'e-wallet'}
                        iconUrl={redemptionTarget?.iconUrl}
                        name={redemptionTarget?.name}
                        nickname={redemptionTarget?.nickname}
                        accountNumber={redemptionTarget?.accountNumber}
                        provider={redemptionTarget?.provider}
                        isValid={true}
                        onClick={() => { }}
                    />
                </SelectedAccountContainer>
                {memo && <DetailsContainer>
                    <SectionLabel>{t('redeem.confirmation.details.sectionLabel')}</SectionLabel>
                    <DetailsBox>
                        <ConfirmationInformationRow
                            title={t('redeem.confirmation.details.memo')}
                            value={memo}
                            boldValue
                        />
                    </DetailsBox>
                </DetailsContainer>}
                {type === 'e-wallet' && (
                    <AlertContainer>
                        <AlertBox
                            alertText={t('redeem.confirmation.eWalletAlert')}
                            borderColor="transparent"
                            backgroundColor="transparent"
                            iconFillColor={CRIMSON_500}
                            iconOutlineColor="transparent"
                        />
                    </AlertContainer>
                )}
            </ContentContainer>
            <FixedButtonContainer>
                <PrimaryButton
                    onClick={requestRedeem}
                >
                    {t('redeem.confirmation.requestRedeemButton')}
                </PrimaryButton>
            </FixedButtonContainer>
        </PageContainer>
    )
}

export default RedeemConfirmation